import {
  DEFAULT_DOMAIN_COLOR_MODE,
  DOMAIN_COLOR_MODES,
  domainColorPreferenceDecision,
} from "./domain-color-preference-repository.js";
import {
  browserDomainColorPreferenceRepository,
} from "./appearance-preferences.js";

const ATTRIBUTE = "data-domain-colors";
const PREFERENCE_STATE_ATTRIBUTE = "data-domain-colors-preference-state";
const GAME = "game";
const THEME_WITH_GAME_GRADES = "theme-with-game-grades";
const THEME = DEFAULT_DOMAIN_COLOR_MODE;
const OPTIONS = Object.freeze([
  Object.freeze({
    value: GAME,
    labelKey: "appearance.domain_colors.game.label",
    label: "Use game colors",
    detailKey: "appearance.domain_colors.game.detail",
    detail: "Grades, ranks, and common status colors use the game palette.",
  }),
  Object.freeze({
    value: THEME_WITH_GAME_GRADES,
    labelKey: "appearance.domain_colors.game_grades.label",
    label: "Use game colors for grades and ranks",
    detailKey: "appearance.domain_colors.game_grades.detail",
    detail: "Status colors follow the theme; item grades and lifeskill ranks keep their game colors.",
  }),
  Object.freeze({
    value: THEME,
    labelKey: "appearance.domain_colors.theme.label",
    label: "Use theme colors",
    detailKey: "appearance.domain_colors.theme.detail",
    detail: "Item grades and lifeskill ranks both follow the active theme.",
  }),
]);

function setAttribute(element, name, value) {
  const exact = String(value);
  if (element?.getAttribute?.(name) !== exact) {
    element?.setAttribute?.(name, exact);
  }
}

function toggleClass(element, name, enabled) {
  if (element?.classList?.contains?.(name) !== enabled) {
    element?.classList?.toggle?.(name, enabled);
  }
}

export function createDomainColorPresentationController({
  repository,
  doc = globalThis.document,
  win = globalThis.window,
} = {}) {
  if (
    !repository
    || typeof repository.setMode !== "function"
    || typeof repository.state !== "function"
    || typeof repository.subscribe !== "function"
  ) {
    throw new TypeError(
      "Domain-color presentation requires the typed repository contract.",
    );
  }
  if (
    !doc?.documentElement
    || typeof doc.addEventListener !== "function"
    || typeof doc.removeEventListener !== "function"
    || typeof doc.querySelectorAll !== "function"
    || !win
  ) {
    throw new TypeError(
      "Domain-color presentation requires a browser document and window.",
    );
  }

  let started = false;
  let disposed = false;
  let unsubscribe = null;
  let currentDecision = domainColorPreferenceDecision(repository.state());
  let acknowledgedDecision = null;

  function translate(key, fallback) {
    const language = win.__fishystuffLanguage;
    if (language && typeof language.t === "function") {
      const translated = language.t(key);
      if (translated && translated !== key) return translated;
    }
    return fallback;
  }

  function setSelectedState(panel, decision) {
    setAttribute(panel, PREFERENCE_STATE_ATTRIBUTE, decision.repositoryKind);
    panel.querySelectorAll("[data-domain-colors-option]").forEach((label) => {
      const input = label.querySelector("input");
      const selected = Boolean(input) && input.value === decision.mode;
      if (input) {
        input.checked = selected;
        input.disabled = !decision.canChange;
      }
      setAttribute(label, "aria-disabled", String(!decision.canChange));
      toggleClass(label, "border-primary", selected);
      toggleClass(label, "bg-primary/8", selected);
      toggleClass(label, "border-base-content/15", !selected);
      toggleClass(label, "bg-base-100", !selected);
    });
  }

  function syncPanels(decision) {
    doc.querySelectorAll("[data-domain-colors-panel]").forEach((panel) => {
      setSelectedState(panel, decision);
    });
  }

  function projectState(state) {
    if (disposed) return;
    const projected = domainColorPreferenceDecision(state);
    const acknowledged = ["ready", "missing", "conflict"].includes(
      projected.repositoryKind,
    );
    if (acknowledged) acknowledgedDecision = projected;
    currentDecision = acknowledged
      ? projected
      : Object.freeze({
        ...projected,
        ...(acknowledgedDecision
          ? {
            revision: acknowledgedDecision.revision,
            mode: acknowledgedDecision.mode,
          }
          : {}),
        canChange: false,
      });
    if (acknowledgedDecision) {
      setAttribute(doc.documentElement, ATTRIBUTE, currentDecision.mode);
    }
    setAttribute(
      doc.documentElement,
      PREFERENCE_STATE_ATTRIBUTE,
      projected.repositoryKind,
    );
    syncPanels(currentDecision);
  }

  function optionMarkup(option, inputName, compact) {
    const detail = compact
      ? ""
      : '<span class="block text-xs leading-5 text-base-content/60">'
        + translate(option.detailKey, option.detail)
        + "</span>";
    return '<label class="label w-full min-w-0 cursor-pointer justify-start gap-3 whitespace-normal rounded-box border border-base-content/15 bg-base-100 px-3 py-2 transition-colors hover:border-base-content/35" data-domain-colors-option>'
      + '<input type="radio" class="radio radio-primary radio-sm shrink-0" name="'
      + inputName
      + '" value="'
      + option.value
      + '">'
      + '<span class="min-w-0 flex-1"><span class="block break-words text-sm font-semibold text-base-content">'
      + translate(option.labelKey, option.label)
      + "</span>"
      + detail
      + "</span></label>";
  }

  function renderPanel(panel, index) {
    const compact = panel.hasAttribute("data-domain-colors-panel-compact");
    const inputName = `domain-colors-${panel.id || String(index + 1)}`;
    const title = translate(
      "appearance.domain_colors.title",
      "Palette sources",
    );
    const description = compact
      ? ""
      : '<p class="text-sm leading-6 text-base-content/65">'
        + translate(
          "appearance.domain_colors.detail",
          "Choose whether source-backed labels use game colors or the active theme.",
        )
        + "</p>";
    panel.innerHTML = '<legend class="fieldset-legend text-sm">'
      + title
      + "</legend>"
      + description
      + '<div class="grid gap-2">'
      + OPTIONS.map((option) => optionMarkup(option, inputName, compact)).join("")
      + "</div>";
    setSelectedState(panel, currentDecision);
  }

  function renderPanels() {
    doc.querySelectorAll("[data-domain-colors-panel]").forEach(renderPanel);
  }

  async function handleChange(event) {
    const input = event?.target;
    if (
      input?.type !== "radio"
      || input.checked !== true
      || input.disabled === true
      || !input.closest?.("[data-domain-colors-panel]")
      || !DOMAIN_COLOR_MODES.includes(input.value)
      || !currentDecision.canChange
    ) {
      return;
    }

    const requestedMode = input.value;
    const expectedRevision = currentDecision.revision;
    // A native radio checks itself before `change`. Restore the repository's
    // projection immediately; only its acknowledgement may change the UI.
    syncPanels(currentDecision);
    try {
      await repository.setMode(requestedMode, {
        expectedRevision,
      });
    } catch (error) {
      globalThis.reportError?.(error);
      projectState(repository.state());
    }
  }

  function handleLanguageChange() {
    if (!disposed) renderPanels();
  }

  function handlePageHide(event) {
    if (event?.persisted !== true) dispose();
  }

  function start() {
    if (disposed || started) return;
    started = true;
    renderPanels();
    doc.addEventListener("change", handleChange);
    win.addEventListener?.("fishystuff:languagechange", handleLanguageChange);
    win.addEventListener?.("pagehide", handlePageHide);
    unsubscribe = repository.subscribe(projectState);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribe?.();
    unsubscribe = null;
    doc.removeEventListener("change", handleChange);
    win.removeEventListener?.("fishystuff:languagechange", handleLanguageChange);
    win.removeEventListener?.("pagehide", handlePageHide);
  }

  return Object.freeze({ dispose, start });
}

function hasDomainColorPresentationBrowserContract(win, doc) {
  return Boolean(
    win
    && doc?.documentElement
    && typeof doc.documentElement.getAttribute === "function"
    && typeof doc.documentElement.setAttribute === "function"
    && typeof doc.addEventListener === "function"
    && typeof doc.removeEventListener === "function"
    && typeof doc.querySelectorAll === "function"
    && typeof win.addEventListener === "function"
    && typeof win.removeEventListener === "function",
  );
}

if (
  hasDomainColorPresentationBrowserContract(
    globalThis.window,
    globalThis.document,
  )
) {
  createDomainColorPresentationController({
    repository: browserDomainColorPreferenceRepository(),
  }).start();
}
